<template>
  <div class="border overflow-hidden mb-6">
    <template
      v-for="(goal, index) in goals"
      :key="index"
    >
      <div
        type="button"
        class="cursor-pointer border-t border-gray-200 focus:ring-4 focus:ring-gray-200 last:border-b-0 hover:bg-gray-50 duration-200"
        @click="toggleGoalAccordion(index)"
      >
        <div class="flex items-center justify-between w-full px-2 py-3 font-medium gap-3">
          <div class="flex items-center">
            <h3 class="flex items-center pl-2.5 text-black duration-200">
              {{ goal.name }}
            </h3>
          </div>

          <div
            class="duration-300 origin-center"
            :class="openGoal[index] ? 'rotate-180' : ''"
          >
            <FontAwesomeIcon
              :icon="faChevronDown"
              class="text-gray-900 pr-1"
            />
          </div>
        </div>
      </div>

      <Transition>
        <div
          v-if="openGoal[index]"
          :id="goal.id"
          class="border-b last:border-b-0 text-gray-600"
        >
          <div
            class="pl-4 pr-4 pb-2"
            v-html="domPurify.sanitize(goal?.description)"
          />
          <template v-if="goal.subgoals.length > 0">
            <template
              v-for="(subgoal, subgoalIndex) in goal.subgoals"
              :key="subgoalIndex"
            >
              <div
                type="button"
                class="cursor-pointer border-t border-gray-300 bg-gray-200 pl-6 pr-2  last:border-b-0 hover:bg-gray-300 duration-200"
                @click="toggleSubgoalAccordion(index, subgoalIndex)"
              >
                <div class="flex items-center justify-between w-full py-3 font-medium gap-3">
                  <div class="flex items-center">
                    <h3 class="flex items-center pl-4 !pb-0 text-black duration-200">
                      {{ subgoal.name }}
                    </h3>
                  </div>

                  <div
                    class="duration-300 origin-center"
                    :class="isSubgoalOpen(index, subgoalIndex) ? 'rotate-180' : ''"
                  >
                    <FontAwesomeIcon
                      :icon="faChevronDown"
                      class="text-gray-900 pr-1"
                    />
                  </div>
                </div>
              </div>

              <Transition>
                <div
                  v-if="isSubgoalOpen(index, subgoalIndex)"
                  :id="subgoal.id"
                  class="border-b last:border-b-0 bg-gray-200 pl-4 pr-2  text-gray-700"
                >
                  <div
                    class="px-6 pb-2"
                    v-html="domPurify.sanitize(subgoal?.description)"
                  />
                </div>
              </Transition>
            </template>
          </template>
        </div>
      </Transition>
    </template>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useDomPurify } from '@/composables';

const domPurify = useDomPurify();

const props = defineProps({
  goals: {
    type: Array,
    default: null,
  },
});

const openGoal = ref([]);
const openSubgoal = ref([]);

// Initialize the open array with false for each goal
if (props.goals) {
  openGoal.value = props.goals.map(() => false);
}

const toggleGoalAccordion = (index) => {
  openGoal.value[index] = !openGoal.value[index];
};

// Create a unique key string for every parent-child combination
const getSubgoalKey = (goalIndex, subgoalIndex) => {
  return `${goalIndex}-${subgoalIndex}`;
};

// Toggle logic uses the unique key
const toggleSubgoalAccordion = (goalIndex, subgoalIndex) => {
  const key = getSubgoalKey(goalIndex, subgoalIndex);
  openSubgoal.value[key] = !openSubgoal.value[key];
};

// Helper to check if open (keeps template clean)
const isSubgoalOpen = (goalIndex, subgoalIndex) => {
  const key = getSubgoalKey(goalIndex, subgoalIndex);
  return !!openSubgoal.value[key];
};

</script>

